/**
 * PublicLeagueTeamsPage — /l/:slug/teams
 * ─────────────────────────────────────────────────────────────────
 * Public pages overhaul P3 (2026-06-09). Team directory grouped by
 * division with an instant filter box — the "very easy to find a
 * team and get to its page" requirement.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { container, Section, Card, EmptyState, INK, MUTED, LINE, SURFACE } from './pubUi.jsx';

export default function PublicLeagueTeamsPage() {
  const { slug } = useParams();
  const { league, accent } = useOutletContext();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (league?.name) document.title = `Teams — ${league.name}`;
  }, [league?.name]);

  useEffect(() => {
    let cancelled = false;
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/teams`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ teams: [] }); });
    return () => { cancelled = true; };
  }, [slug]);

  const grouped = useMemo(() => {
    const teams = data?.teams || [];
    const needle = q.trim().toLowerCase();
    const filtered = needle
      ? teams.filter(t => t.team_name.toLowerCase().includes(needle))
      : teams;
    const map = new Map();
    for (const t of filtered) {
      const key = t.division_name || 'Teams';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return [...map.entries()];
  }, [data, q]);

  return (
    <div style={{ ...container, paddingTop: 36 }}>
      <Section
        eyebrow={data?.season?.name || 'Directory'}
        title="Teams"
        action={
          <input
            type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Filter teams…"
            style={{
              padding: '10px 14px', borderRadius: 12, border: `1px solid ${LINE}`,
              background: '#fff', fontSize: 14, outline: 'none', minWidth: 220,
            }}
          />
        }>
        {data === null && <div style={{ color: MUTED }}>Loading…</div>}
        {data !== null && grouped.length === 0 && (
          <EmptyState icon="🏒">
            {q.trim() ? `No team matches "${q.trim()}".` : 'No teams enrolled yet.'}
          </EmptyState>
        )}
        {grouped.map(([division, teams]) => (
          <div key={division} style={{ marginBottom: 28 }}>
            {grouped.length > 1 || division !== 'Teams' ? (
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED, margin: '0 0 12px' }}>
                {division}
              </div>
            ) : null}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
              {teams.map(t => (
                <Link key={t.team_id} to={`/l/${slug}/team/${t.team_id}`} style={{ textDecoration: 'none', color: INK }}>
                  <Card hover style={{ padding: '18px 18px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{
                      width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                      background: SURFACE, border: `1px solid ${LINE}`,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: 16, color: accent,
                    }}>
                      {t.team_name.slice(0, 2).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 800, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.team_name}
                      </span>
                      {t.division_name && (
                        <span style={{ fontSize: 12, color: MUTED }}>{t.division_name}</span>
                      )}
                    </span>
                    <span style={{ color: accent, fontWeight: 800 }}>→</span>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}
